import * as React from 'react';
import { makeStyles } from '@material-ui/core';
import theme from '../../theme';

import getCalendarDates from './utils';
import CalendarBlock from './calendarBlock';
import { Check } from '@material-ui/icons';
import { getStartEndDate, getStartMonthDayAndEndMonthDay } from "../../utils"


const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    background: theme.colors.White,
    flexDirection: "row",
    fontFamily: theme.fontFamily.MARKPROLIGHT,
    width: "800px",
    borderRadius: "5px",
    border: "1px solid whitesmoke"
  },
  sidePane: {
    "& div": {
      borderRadius: "8px",
      transition: ".2s ease-in",
      padding: "10px",
      display: "flex",
      flexDirection: "row",
      flexWrap: "nowrap",
      justifyContent: "space-between",
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#eee"
      },
      marginTop: "2px"
    },
    width: "220px",
    padding: "12px",
  },
  glassSelectedEffect: {
    cursor: "pointer",
    backgroundColor: "#eee",
    fontWeight: "bolder",
  },
  leftPane: {
    width: "calc(800px - 220px)",
    display: "flex",
    flexDirection: "column",
    borderLeft: "1px solid whitesmoke"
  },
  firstBlock: {
    width: "50%",
    padding: "15px",
    borderRight: "1px solid whitesmoke"
  },
  secondBlock: {
    width: "50%",
    padding: "15px"
  },
  blockGrp: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    height: "100%"
  },
  thirdBlock: {
    borderTop: "1px solid whitesmoke",
    padding: '10px',
    fontWeight: 'bold',
    fontSize: '16px',
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    alignItems: "center"
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '5px',
    textAlign: 'center',
    fontSize: '14px',
  },
  dayHeader: {
    fontWeight: 'bold',
    borderBottom: '1px solid #ccc',
    paddingBottom: '5px',
  },
  dateCell: {
    padding: '5px',
    borderRadius: '4px',
    "&:hover": {
      backgroundColor: "#eee",
      cursor: "pointer"
    }
  },
  selected: {
    backgroundColor: "#3399ff",
    color: "white",
    borderRadius: "4px"
  },
  applyBtn: {
    backgroundColor: theme.colors.DodgerBlue,
    color: theme.colors.White,
    border: "none",
    outline: "none",
    cursor: "pointer",
    marginLeft: "2px"
  }
}));

const sidePaneValue = [
  { label: "Default", type: "default", value: null },
  { label: "Last 7 days", type: "days", value: 7 },
  { label: "Last 4 weeks", type: "days", value: 28 },
  { label: "Last 3 months", type: "months", value: 3 },
  { label: "Last 12 months", type: "months", value: 12 },
  { label: "Month to date", type: "mtd" },
  { label: "Month to End", type: "mte" },
  { label: "Quarter to date", type: "qtd" },
  { label: "Year to date", type: "ytd" }
];


export default function CustomDateRangePicker(
  {
    startDateValue,
    setStartDateValue,
    endDateValue,
    setEndDateValue
  }
) {
  const classes = useStyles();
  const formatToDateInputValue = (date) => {
    if (!date?.day || isNaN(date?.month) || !date?.year) return '';
    const dt = new Date(date.year, date.month, date.day);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');

    let formatted = `${yyyy}-${mm}-${dd}`;
    return formatted;
  };

  const [monthsOffset, setMonthsOffset] = React.useState({ first: - 1, second: 0 })
  const [sidePaneIndex, setSidePaneIndex] = React.useState(null)
  const [start, end] = startDateValue && endDateValue ? [new Date(startDateValue), new Date(endDateValue)] : getStartEndDate();
  const [selectedStartDate, setSelectedStartDate] = React.useState(() => {

    return {
      day: start.getDate(),
      month: start.getMonth(),
      year: start.getFullYear()
    }
  })
  const [selectedEndDate, setSelectedEndDate] = React.useState(() => {
    return {
      day: end.getDate(),
      month: end.getMonth(),
      year: end.getFullYear()
    }
  }
  )
  const [temporaryHover, setTemporaryHover] = React.useState({ overActive: false, day: 0, month: 0, year: 0 });
  const [startDateFormat, setStartDateFormat] = React.useState(formatToDateInputValue(selectedStartDate));
  const [endDateFormat, setEndDateFormat] = React.useState(formatToDateInputValue(selectedEndDate));
  const [startLocalDateValue, setStartLocalDateValue] = React.useState('');
  const [endLocalDateValue, setEndLocalDateValue] = React.useState('');

  const handleNextMonth = () => {
    setMonthsOffset(value => ({ first: ++value.first, second: ++value.second }))
  }
  const handlePrevMonth = () => {
    setMonthsOffset(value => ({ first: --value.first, second: --value.second }))
  }
  const handleStartDateInput = (date) => {
    const dt = new Date(date)
    setSelectedStartDate({
      year: dt.getFullYear(),
      month: dt.getMonth(),
      day: dt.getDate()
    })
  }
  const handleEndDateInput = (date) => {
    const dt = new Date(date)
    setSelectedEndDate({
      year: dt.getFullYear(),
      month: dt.getMonth(),
      day: dt.getDate()
    })
  }


  React.useEffect(() => {
    if (selectedStartDate?.day && selectedStartDate.month && selectedStartDate.year) {
      setStartDateFormat(formatToDateInputValue(selectedStartDate));
    };
    if (selectedEndDate?.day && selectedEndDate.month && selectedEndDate.year) {
      setEndDateFormat(formatToDateInputValue(selectedEndDate));
    };
    setStartLocalDateValue('');
    setEndLocalDateValue('');

    if (
      setStartDateValue &&
      setEndDateValue &&
      selectedStartDate.day &&
      selectedEndDate.day
    ) {
      const startDate = new Date(
        selectedStartDate.year,
        selectedStartDate.month,
        selectedStartDate.day
      );
      const endDate = new Date(
        selectedEndDate.year,
        selectedEndDate.month,
        selectedEndDate.day
      );
      if (startDate < endDate) {
        setStartDateValue(formatToDateInputValue(selectedStartDate))
        setEndDateValue(formatToDateInputValue(selectedEndDate))
        // performs NECESSARY BY PARENT 
      }

    }
  }, [selectedEndDate, selectedStartDate])
  return (
    <div className={classes.root}>
      <div className={classes.sidePane}>
        {sidePaneValue.map((value, i) => {
          return (
            <div
              className={sidePaneIndex === i ? classes.glassSelectedEffect : ''}
              key={i}
              onClick={() => {
                let now = new Date();
                let start = new Date();

                switch (value.type) {
                  case 'days':
                    start.setDate(now.getDate() - value.value);
                    break;
                  case 'months':
                    start.setMonth(now.getMonth() - value.value);
                    break;
                  case 'mtd':
                    start = new Date(now.getFullYear(), now.getMonth(), 1);
                    break;
                  case 'qtd':
                    const quarter = Math.floor(now.getMonth() / 3);
                    start = new Date(now.getFullYear(), quarter * 3, 1);
                    break;
                  case 'ytd':
                    start = new Date(now.getFullYear(), 0, 1);
                    break;
                  case 'mte':
                    [start, now] = getStartMonthDayAndEndMonthDay();
                    break;
                  default:
                    const obj = getStartEndDate();
                    start = obj[0];
                    break;
                }

                setSelectedStartDate({
                  day: start.getDate(),
                  month: start.getMonth(),
                  year: start.getFullYear()
                });
                setSelectedEndDate({
                  day: now.getDate(),
                  month: now.getMonth(),
                  year: now.getFullYear()
                });

                setSidePaneIndex(i);
              }}
            >
              {value.label}
              {sidePaneIndex === i ? (
                <Check style={{ color: theme.colors.DodgerBlueLight }} />
              ) : null}
            </div>
          );
        })}

      </div>
      <div className={classes.leftPane}>
        <div className={classes.blockGrp}>
          <div className={classes.firstBlock}>
            <CalendarBlock
              data={getCalendarDates(monthsOffset.first)}
              prevAction={handlePrevMonth}
              selectedDate={selectedStartDate}
              onDateClick={setSelectedStartDate}
              setDateEnd={setSelectedEndDate}
              selectedEndDate={selectedEndDate}
              temporaryHover={temporaryHover}
              setTemporaryHover={setTemporaryHover}
            />
          </div>
          <div className={classes.secondBlock}>
            <CalendarBlock
              selectedDate={selectedStartDate}
              onDateClick={setSelectedStartDate}
              nextAction={handleNextMonth}
              data={getCalendarDates(monthsOffset.second)}
              setDateEnd={setSelectedEndDate}
              selectedEndDate={selectedEndDate}
              temporaryHover={temporaryHover}
              setTemporaryHover={setTemporaryHover}
            />
          </div>
        </div>
        <div className={classes.thirdBlock}>
          <div>
            <input
              type='date'
              onChange={(e) => {
                setSidePaneIndex(null)
                console.log(e.target.value)
                setStartLocalDateValue(e.target.value)
                // handleStartDateInput(e.target.value)
              }}
            /> -&gt;&nbsp;
            <input
              type='date'
              onChange={(e) => {
                setSidePaneIndex(null)
                // handleEndDateInput(e.target.value)
                setEndLocalDateValue(e.target.value)
              }}
            />
          </div>
          <input
            className={classes.applyBtn}
            onClick={(e) => {
              e.preventDefault();
              handleStartDateInput(startLocalDateValue)
              handleEndDateInput(endLocalDateValue)
            }} type='button' value={'Apply'} />
        </div>
      </div>
    </div>
  );
}
